import { useCallback, useState } from "react";
import { fetchLgas, fetchStates } from "../api.js";

/**
 * State and LGA lists for the portal.
 *
 * `App.tsx` holds the same thing for the form app, where it can decide up front whether the form
 * even has a state question. The portal cannot: it does not know which form it is about to open
 * for editing until someone presses the button. So the lists are fetched on demand — the first
 * state field to render asks for the states, and choosing one asks for that state's LGAs.
 */
export const usePortalLookups = (): {
  states: { code: string; name: string }[];
  lgasForState: (stateName: string) => { id: string; name: string }[];
  onStateSelected: (state: string) => void;
} => {
  const [states, setStates] = useState<{ code: string; name: string }[]>([]);
  const [lgasByState, setLgasByState] = useState<Record<string, { id: string; name: string }[]>>(
    {},
  );
  const [askedForStates, setAskedForStates] = useState(false);

  const onStateSelected = useCallback(
    (state: string) => {
      if (!askedForStates) {
        setAskedForStates(true);
        void fetchStates()
          .then((result) => setStates(result.data))
          .catch(() => undefined);
      }
      if (lgasByState[state]) return;
      void fetchLgas(state)
        .then((result) => setLgasByState((current) => ({ ...current, [state]: result.data })))
        .catch(() => undefined);
    },
    [askedForStates, lgasByState],
  );

  const lgasForState = useCallback(
    (stateName: string) => lgasByState[stateName] ?? [],
    [lgasByState],
  );

  return { states, lgasForState, onStateSelected };
};
